import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Grid2 as Grid,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Star as StarIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  cmsService,
  CMSPage,
  CMSHero,
  CMSFeature,
  CMSHeroInput,
  CMSFeatureInput,
} from '../../../services';
import {
  CMSPlan,
  CMSPlanInput,
  CMSFAQ,
  CMSFAQInput,
  CMSCTA,
  CMSCTAInput,
} from '../../../services/cms.service';
import { Switch, FormControlLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const PageEditor: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const pageId = parseInt(id!);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tab, setTab] = useState(0);

  const [page, setPage] = useState<CMSPage | null>(null);
  const [hero, setHero] = useState<CMSHero | null>(null);
  const [features, setFeatures] = useState<CMSFeature[]>([]);
  const [plans, setPlans] = useState<CMSPlan[]>([]);
  const [faqs, setFaqs] = useState<CMSFAQ[]>([]);
  const [ctas, setCtas] = useState<CMSCTA[]>([]);

  // Form states
  const [pageForm, setPageForm] = useState({
    slug: '',
    titulo: '',
    meta_descricao: '',
    meta_keywords: '',
    status: 'rascunho' as 'rascunho' | 'publicado',
  });

  const [heroForm, setHeroForm] = useState<CMSHeroInput>({
    badge_texto: '',
    titulo: '',
    subtitulo: '',
    descricao: '',
    btn_primario_texto: '',
    btn_primario_link: '',
    btn_secundario_texto: '',
    btn_secundario_link: '',
    trust_badge_texto: '',
    trust_badge_rating: 5,
    imagem_fundo: '',
    status: 'ativo',
  });

  const [featureDialog, setFeatureDialog] = useState<{
    open: boolean;
    feature: CMSFeature | null;
  }>({ open: false, feature: null });

  const [featureForm, setFeatureForm] = useState<CMSFeatureInput>({
    icone: '',
    titulo: '',
    descricao: '',
    link: '',
    cor_fundo: 'bg-blue-100',
    cor_texto: 'text-blue-600',
    ordem: 0,
    status: 'ativo',
  });

  // Plans dialog
  const [planDialog, setPlanDialog] = useState<{ open: boolean; plan: CMSPlan | null }>({ open: false, plan: null });
  const [planForm, setPlanForm] = useState<CMSPlanInput>({
    nome: '', subtitulo: '', preco: undefined, moeda: 'CVE', intervalo: 'mês',
    badge: '', btn_texto: '', btn_link: '', destaque: false, ordem: 0, status: 'ativo',
    features: [],
  });
  const [planFeaturesText, setPlanFeaturesText] = useState('');

  // FAQ dialog
  const [faqDialog, setFaqDialog] = useState<{ open: boolean; faq: CMSFAQ | null }>({ open: false, faq: null });
  const [faqForm, setFaqForm] = useState<CMSFAQInput>({
    pagina_id: pageId, categoria: '', pergunta: '', resposta: '', ordem: 0, status: 'ativo',
  });

  // CTA dialog
  const [ctaDialog, setCtaDialog] = useState<{ open: boolean; cta: CMSCTA | null }>({ open: false, cta: null });
  const [ctaForm, setCtaForm] = useState<CMSCTAInput>({
    posicao: 'final', titulo: '', descricao: '', btn_texto: '', btn_link: '',
    cor_fundo: 'pagali-primary', ordem: 0, status: 'ativo',
  });

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const [pageRes, heroRes, featuresRes, plansRes, faqsRes, ctasRes] = await Promise.all([
        cmsService.getPage(pageId),
        cmsService.getHero(pageId),
        cmsService.getFeatures(pageId),
        cmsService.getPricing(pageId),
        cmsService.getFAQ(pageId),
        cmsService.getCTAs(pageId),
      ]);
      setPlans(plansRes.data.data || []);
      setFaqs(faqsRes.data.data || []);
      setCtas(ctasRes.data.data || []);

      const pageData = pageRes.data.data;
      setPage(pageData);
      setPageForm({
        slug: pageData.slug,
        titulo: pageData.titulo,
        meta_descricao: pageData.meta_descricao || '',
        meta_keywords: pageData.meta_keywords || '',
        status: pageData.status,
      });

      const heroData = heroRes.data.data;
      if (heroData) {
        setHero(heroData);
        setHeroForm({
          badge_texto: heroData.badge_texto || '',
          titulo: heroData.titulo,
          subtitulo: heroData.subtitulo || '',
          descricao: heroData.descricao || '',
          btn_primario_texto: heroData.btn_primario_texto || '',
          btn_primario_link: heroData.btn_primario_link || '',
          btn_secundario_texto: heroData.btn_secundario_texto || '',
          btn_secundario_link: heroData.btn_secundario_link || '',
          trust_badge_texto: heroData.trust_badge_texto || '',
          trust_badge_rating: heroData.trust_badge_rating || 5,
          imagem_fundo: heroData.imagem_fundo || '',
          status: heroData.status,
        });
      }

      setFeatures(featuresRes.data.data || []);
    } catch (err: any) {
      setError(err.message || t('cms.errorLoadingPage'));
    } finally {
      setLoading(false);
    }
  }, [pageId]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const handleSavePage = async () => {
    setSaving(true);
    setError(null);
    try {
      await cmsService.updatePage(pageId, pageForm);
      setSuccess(t('cms.pageSaved'));
    } catch (err: any) {
      setError(err.message || t('cms.errorSavingPage'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHero = async () => {
    setSaving(true);
    setError(null);
    try {
      await cmsService.updateHero(pageId, heroForm);
      setSuccess(t('cms.heroSaved'));
    } catch (err: any) {
      setError(err.message || t('cms.errorSavingHero'));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenFeatureDialog = (feature?: CMSFeature) => {
    if (feature) {
      setFeatureForm({
        icone: feature.icone,
        titulo: feature.titulo,
        descricao: feature.descricao || '',
        link: feature.link || '',
        cor_fundo: feature.cor_fundo || 'bg-blue-100',
        cor_texto: feature.cor_texto || 'text-blue-600',
        ordem: feature.ordem,
        status: feature.status,
      });
      setFeatureDialog({ open: true, feature });
    } else {
      setFeatureForm({
        icone: 'Star',
        titulo: '',
        descricao: '',
        link: '',
        cor_fundo: 'bg-blue-100',
        cor_texto: 'text-blue-600',
        ordem: features.length,
        status: 'ativo',
      });
      setFeatureDialog({ open: true, feature: null });
    }
  };

  const handleSaveFeature = async () => {
    setSaving(true);
    setError(null);
    try {
      if (featureDialog.feature) {
        await cmsService.updateFeature(featureDialog.feature.id, featureForm);
      } else {
        await cmsService.createFeature(pageId, featureForm);
      }
      setFeatureDialog({ open: false, feature: null });
      setSuccess(t('cms.featureSaved'));
      fetchPage();
    } catch (err: any) {
      setError(err.message || t('cms.errorSavingFeature'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFeature = async (feature: CMSFeature) => {
    if (!window.confirm(t('cms.confirmDeleteFeature', { name: feature.titulo }))) return;
    try {
      await cmsService.deleteFeature(feature.id);
      setSuccess(t('cms.featureDeleted'));
      fetchPage();
    } catch (err: any) {
      setError(err.message || t('cms.errorDeletingFeature'));
    }
  };

  // ====== PLANS ======
  const handleOpenPlanDialog = (plan?: CMSPlan) => {
    if (plan) {
      setPlanForm({
        nome: plan.nome,
        subtitulo: plan.subtitulo || '',
        preco: plan.preco,
        moeda: plan.moeda || 'CVE',
        intervalo: plan.intervalo || 'mês',
        badge: plan.badge || '',
        btn_texto: plan.btn_texto || '',
        btn_link: plan.btn_link || '',
        destaque: plan.destaque,
        ordem: plan.ordem,
        status: plan.status,
        features: plan.features || [],
      });
      setPlanFeaturesText((plan.features || []).map((f: any) => `${f.incluido === 0 || f.incluido === false ? '- ' : ''}${f.texto}`).join('\n'));
      setPlanDialog({ open: true, plan });
    } else {
      setPlanForm({
        nome: '', subtitulo: '', preco: undefined, moeda: 'CVE', intervalo: 'mês',
        badge: '', btn_texto: '', btn_link: '', destaque: false, ordem: plans.length, status: 'ativo', features: [],
      });
      setPlanFeaturesText('');
      setPlanDialog({ open: true, plan: null });
    }
  };

  const handleSavePlan = async () => {
    setSaving(true);
    try {
      const featuresArr = planFeaturesText.split('\n').filter(l => l.trim()).map(l => ({
        texto: l.replace(/^-\s*/, '').trim(),
        incluido: !l.startsWith('-'),
      }));
      const data = { ...planForm, features: featuresArr };
      if (planDialog.plan) {
        await cmsService.updatePlan(planDialog.plan.id, data);
      } else {
        await cmsService.createPlan(pageId, data);
      }
      setPlanDialog({ open: false, plan: null });
      setSuccess('Plano guardado');
      fetchPage();
    } catch (err: any) {
      setError(err.message || 'Erro ao guardar plano');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlan = async (plan: CMSPlan) => {
    if (!window.confirm(`Eliminar plano "${plan.nome}"?`)) return;
    try {
      await cmsService.deletePlan(plan.id);
      setSuccess('Plano eliminado');
      fetchPage();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // ====== FAQ ======
  const handleOpenFaqDialog = (faq?: CMSFAQ) => {
    if (faq) {
      setFaqForm({
        pagina_id: pageId,
        categoria: faq.categoria || '',
        pergunta: faq.pergunta,
        resposta: faq.resposta,
        ordem: faq.ordem,
        status: faq.status,
      });
      setFaqDialog({ open: true, faq });
    } else {
      setFaqForm({ pagina_id: pageId, categoria: '', pergunta: '', resposta: '', ordem: faqs.length, status: 'ativo' });
      setFaqDialog({ open: true, faq: null });
    }
  };

  const handleSaveFaq = async () => {
    setSaving(true);
    try {
      if (faqDialog.faq) {
        await cmsService.updateFAQ(faqDialog.faq.id, faqForm);
      } else {
        await cmsService.createFAQ(faqForm);
      }
      setFaqDialog({ open: false, faq: null });
      setSuccess('FAQ guardada');
      fetchPage();
    } catch (err: any) {
      setError(err.message || 'Erro ao guardar FAQ');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFaq = async (faq: CMSFAQ) => {
    if (!window.confirm(`Eliminar pergunta "${faq.pergunta.substring(0, 50)}..."?`)) return;
    try {
      await cmsService.deleteFAQ(faq.id);
      setSuccess('FAQ eliminada');
      fetchPage();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // ====== CTAs ======
  const handleOpenCtaDialog = (cta?: CMSCTA) => {
    if (cta) {
      setCtaForm({
        posicao: cta.posicao || 'final',
        titulo: cta.titulo,
        descricao: cta.descricao || '',
        btn_texto: cta.btn_texto || '',
        btn_link: cta.btn_link || '',
        cor_fundo: cta.cor_fundo || 'pagali-primary',
        ordem: cta.ordem,
        status: cta.status,
      });
      setCtaDialog({ open: true, cta });
    } else {
      setCtaForm({ posicao: 'final', titulo: '', descricao: '', btn_texto: '', btn_link: '', cor_fundo: 'pagali-primary', ordem: ctas.length, status: 'ativo' });
      setCtaDialog({ open: true, cta: null });
    }
  };

  const handleSaveCta = async () => {
    setSaving(true);
    try {
      if (ctaDialog.cta) {
        await cmsService.updateCTA(ctaDialog.cta.id, ctaForm);
      } else {
        await cmsService.createCTA(pageId, ctaForm);
      }
      setCtaDialog({ open: false, cta: null });
      setSuccess('CTA guardada');
      fetchPage();
    } catch (err: any) {
      setError(err.message || 'Erro ao guardar CTA');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCta = async (cta: CMSCTA) => {
    if (!window.confirm(`Eliminar CTA "${cta.titulo}"?`)) return;
    try {
      await cmsService.deleteCTA(cta.id);
      setSuccess('CTA eliminada');
      fetchPage();
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/cms/pages')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {page?.titulo || t('cms.pageEditor')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              /{page?.slug}
            </Typography>
          </Box>
        </Box>
        <Chip
          label={pageForm.status === 'publicado' ? t('cms.published') : t('cms.draft')}
          color={pageForm.status === 'publicado' ? 'success' : 'default'}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label={t('cms.generalSeo')} />
          <Tab label={t('cms.hero')} />
          <Tab label={t('cms.features')} />
          <Tab label={`Planos (${plans.length})`} />
          <Tab label={`FAQ (${faqs.length})`} />
          <Tab label={`CTAs (${ctas.length})`} />
        </Tabs>
      </Paper>

      {/* Tab: General */}
      <TabPanel value={tab} index={0}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('cms.pageInfo')}
            </Typography>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('cms.titleLabel')}
                  value={pageForm.titulo}
                  onChange={(e) => setPageForm(prev => ({ ...prev, titulo: e.target.value }))}
                  margin="normal"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('cms.slugUrl')}
                  value={pageForm.slug}
                  onChange={(e) => setPageForm(prev => ({ ...prev, slug: e.target.value }))}
                  margin="normal"
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={t('cms.metaDescription')}
                  value={pageForm.meta_descricao}
                  onChange={(e) => setPageForm(prev => ({ ...prev, meta_descricao: e.target.value }))}
                  margin="normal"
                  multiline
                  rows={2}
                  helperText={t('cms.metaDescriptionChars', { count: pageForm.meta_descricao.length })}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={t('cms.metaKeywords')}
                  value={pageForm.meta_keywords}
                  onChange={(e) => setPageForm(prev => ({ ...prev, meta_keywords: e.target.value }))}
                  margin="normal"
                  placeholder={t('cms.metaKeywordsPlaceholder')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  select
                  label={t('common.status')}
                  value={pageForm.status}
                  onChange={(e) => setPageForm(prev => ({ ...prev, status: e.target.value as any }))}
                  margin="normal"
                >
                  <MenuItem value="rascunho">{t('cms.draft')}</MenuItem>
                  <MenuItem value="publicado">{t('cms.published')}</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            <Box mt={3}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSavePage}
                disabled={saving}
              >
                {saving ? t('common.saving') : t('cms.savePage')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab: Hero */}
      <TabPanel value={tab} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('cms.heroSection')}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {t('cms.heroSectionDescription')}
            </Typography>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('cms.badgeSmallText')}
                  value={heroForm.badge_texto}
                  onChange={(e) => setHeroForm(prev => ({ ...prev, badge_texto: e.target.value }))}
                  margin="normal"
                  placeholder={t('cms.badgePlaceholderHero')}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={t('cms.mainTitle')}
                  value={heroForm.titulo}
                  onChange={(e) => setHeroForm(prev => ({ ...prev, titulo: e.target.value }))}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={t('cms.subtitleField')}
                  value={heroForm.subtitulo}
                  onChange={(e) => setHeroForm(prev => ({ ...prev, subtitulo: e.target.value }))}
                  margin="normal"
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={t('cms.descriptionField')}
                  value={heroForm.descricao}
                  onChange={(e) => setHeroForm(prev => ({ ...prev, descricao: e.target.value }))}
                  margin="normal"
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid size={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  {t('cms.buttons')}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('cms.primaryBtnText')}
                  value={heroForm.btn_primario_texto}
                  onChange={(e) => setHeroForm(prev => ({ ...prev, btn_primario_texto: e.target.value }))}
                  margin="normal"
                  placeholder={t('cms.primaryBtnTextPlaceholder')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('cms.primaryBtnLink')}
                  value={heroForm.btn_primario_link}
                  onChange={(e) => setHeroForm(prev => ({ ...prev, btn_primario_link: e.target.value }))}
                  margin="normal"
                  placeholder={t('cms.primaryBtnLinkPlaceholder')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('cms.secondaryBtnText')}
                  value={heroForm.btn_secundario_texto}
                  onChange={(e) => setHeroForm(prev => ({ ...prev, btn_secundario_texto: e.target.value }))}
                  margin="normal"
                  placeholder={t('cms.secondaryBtnTextPlaceholder')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('cms.secondaryBtnLink')}
                  value={heroForm.btn_secundario_link}
                  onChange={(e) => setHeroForm(prev => ({ ...prev, btn_secundario_link: e.target.value }))}
                  margin="normal"
                  placeholder={t('cms.secondaryBtnLinkPlaceholder')}
                />
              </Grid>

              <Grid size={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  {t('cms.trustBadge')}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <TextField
                  fullWidth
                  label={t('cms.trustBadgeText')}
                  value={heroForm.trust_badge_texto}
                  onChange={(e) => setHeroForm(prev => ({ ...prev, trust_badge_texto: e.target.value }))}
                  margin="normal"
                  placeholder={t('cms.trustBadgePlaceholder')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('cms.ratingStars')}
                  value={heroForm.trust_badge_rating}
                  onChange={(e) => setHeroForm(prev => ({ ...prev, trust_badge_rating: parseInt(e.target.value) || 5 }))}
                  margin="normal"
                  InputProps={{ inputProps: { min: 1, max: 5 } }}
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  fullWidth
                  label={t('cms.backgroundImage')}
                  value={heroForm.imagem_fundo}
                  onChange={(e) => setHeroForm(prev => ({ ...prev, imagem_fundo: e.target.value }))}
                  margin="normal"
                  placeholder="https://..."
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  select
                  label={t('common.status')}
                  value={heroForm.status}
                  onChange={(e) => setHeroForm(prev => ({ ...prev, status: e.target.value as any }))}
                  margin="normal"
                >
                  <MenuItem value="ativo">{t('common.active')}</MenuItem>
                  <MenuItem value="inativo">{t('common.inactive')}</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            <Box mt={3}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveHero}
                disabled={saving}
              >
                {saving ? t('common.saving') : t('cms.saveHero')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab: Features */}
      <TabPanel value={tab} index={2}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Box>
                <Typography variant="h6">
                  {t('cms.featuresTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('cms.featuresSubtitle')}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenFeatureDialog()}
              >
                {t('cms.newFeature')}
              </Button>
            </Box>

            {features.length === 0 ? (
              <Box textAlign="center" py={5}>
                <StarIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">
                  {t('cms.noFeatureAdded')}
                </Typography>
              </Box>
            ) : (
              <List>
                {features.map((feature, index) => (
                  <ListItem
                    key={feature.id}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <Box sx={{ mr: 2, color: 'text.secondary' }}>
                      <DragIcon />
                    </Box>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography fontWeight="medium">{feature.titulo}</Typography>
                          <Chip size="small" label={feature.icone} variant="outlined" />
                          {feature.status === 'inativo' && (
                            <Chip size="small" label={t('common.inactive')} color="default" />
                          )}
                        </Box>
                      }
                      secondary={feature.descricao?.substring(0, 100)}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title={t('common.edit')}>
                        <IconButton onClick={() => handleOpenFeatureDialog(feature)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete')}>
                        <IconButton color="error" onClick={() => handleDeleteFeature(feature)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab: Plans */}
      <TabPanel value={tab} index={3}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Box>
                <Typography variant="h6">Planos & Preços</Typography>
                <Typography variant="body2" color="text.secondary">
                  Adicione planos com preços e funcionalidades incluídas
                </Typography>
              </Box>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenPlanDialog()}>
                Novo Plano
              </Button>
            </Box>

            {plans.length === 0 ? (
              <Box textAlign="center" py={5}>
                <InfoIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">Nenhum plano criado</Typography>
              </Box>
            ) : (
              <List>
                {plans.map((plan) => (
                  <ListItem key={plan.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                    <Box sx={{ mr: 2, color: 'text.secondary' }}><DragIcon /></Box>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography fontWeight="medium">{plan.nome}</Typography>
                          {plan.destaque && <Chip size="small" label="Destaque" color="primary" />}
                          {plan.badge && <Chip size="small" label={plan.badge} color="secondary" />}
                          {plan.status === 'inativo' && (
                            <Chip size="small" label={t('common.inactive')} color="default" />
                          )}
                        </Box>
                      }
                      secondary={`${plan.subtitulo || ''} • ${plan.preco !== null && plan.preco !== undefined ? `${plan.preco} ${plan.moeda}/${plan.intervalo}` : (plan.intervalo || '')} • ${plan.features?.length || 0} feature(s)`}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title={t('common.edit')}>
                        <IconButton onClick={() => handleOpenPlanDialog(plan)}><EditIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete')}>
                        <IconButton color="error" onClick={() => handleDeletePlan(plan)}><DeleteIcon /></IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab: FAQ */}
      <TabPanel value={tab} index={4}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Box>
                <Typography variant="h6">Perguntas Frequentes</Typography>
                <Typography variant="body2" color="text.secondary">
                  Gerir as perguntas e respostas desta página
                </Typography>
              </Box>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenFaqDialog()}>
                Nova Pergunta
              </Button>
            </Box>

            {faqs.length === 0 ? (
              <Box textAlign="center" py={5}>
                <InfoIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">Nenhuma FAQ criada</Typography>
              </Box>
            ) : (
              <List>
                {faqs.map((faq) => (
                  <ListItem key={faq.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography fontWeight="medium">{faq.pergunta}</Typography>
                          {faq.categoria && <Chip size="small" label={faq.categoria} variant="outlined" />}
                          {faq.status === 'inativo' && (
                            <Chip size="small" label={t('common.inactive')} color="default" />
                          )}
                        </Box>
                      }
                      secondary={faq.resposta?.substring(0, 150)}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title={t('common.edit')}>
                        <IconButton onClick={() => handleOpenFaqDialog(faq)}><EditIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete')}>
                        <IconButton color="error" onClick={() => handleDeleteFaq(faq)}><DeleteIcon /></IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab: CTAs */}
      <TabPanel value={tab} index={5}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Box>
                <Typography variant="h6">Call-to-Actions (CTAs)</Typography>
                <Typography variant="body2" color="text.secondary">
                  Banners de chamada para acção (meio ou final da página)
                </Typography>
              </Box>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenCtaDialog()}>
                Novo CTA
              </Button>
            </Box>

            {ctas.length === 0 ? (
              <Box textAlign="center" py={5}>
                <InfoIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">Nenhum CTA criado</Typography>
              </Box>
            ) : (
              <List>
                {ctas.map((cta) => (
                  <ListItem key={cta.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography fontWeight="medium">{cta.titulo}</Typography>
                          {cta.posicao && <Chip size="small" label={cta.posicao} variant="outlined" />}
                          {cta.status === 'inativo' && (
                            <Chip size="small" label={t('common.inactive')} color="default" />
                          )}
                        </Box>
                      }
                      secondary={`${cta.descricao?.substring(0, 100) || ''} • Botão: ${cta.btn_texto || '-'}`}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title={t('common.edit')}>
                        <IconButton onClick={() => handleOpenCtaDialog(cta)}><EditIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete')}>
                        <IconButton color="error" onClick={() => handleDeleteCta(cta)}><DeleteIcon /></IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Plan Dialog */}
      <Dialog open={planDialog.open} onClose={() => setPlanDialog({ open: false, plan: null })} maxWidth="md" fullWidth>
        <DialogTitle>{planDialog.plan ? 'Editar Plano' : 'Novo Plano'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Nome" value={planForm.nome}
                onChange={(e) => setPlanForm(p => ({ ...p, nome: e.target.value }))} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Subtítulo" value={planForm.subtitulo}
                onChange={(e) => setPlanForm(p => ({ ...p, subtitulo: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label="Preço (vazio = Personalizado)" type="number" value={planForm.preco ?? ''}
                onChange={(e) => setPlanForm(p => ({ ...p, preco: e.target.value === '' ? undefined : parseFloat(e.target.value) }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label="Moeda" value={planForm.moeda}
                onChange={(e) => setPlanForm(p => ({ ...p, moeda: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label="Intervalo" value={planForm.intervalo}
                onChange={(e) => setPlanForm(p => ({ ...p, intervalo: e.target.value }))}
                placeholder="mês, por bilhete, ano..." />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Badge (opcional)" value={planForm.badge}
                onChange={(e) => setPlanForm(p => ({ ...p, badge: e.target.value }))}
                placeholder="Ex: Popular, Recomendado" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Texto do Botão" value={planForm.btn_texto}
                onChange={(e) => setPlanForm(p => ({ ...p, btn_texto: e.target.value }))} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Link do Botão" value={planForm.btn_link}
                onChange={(e) => setPlanForm(p => ({ ...p, btn_link: e.target.value }))} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth multiline rows={6}
                label="Features (uma por linha — prefixar com '- ' para feature não incluída)"
                value={planFeaturesText}
                onChange={(e) => setPlanFeaturesText(e.target.value)}
                helperText="Ex: Até 500 pessoas | - Sem pulseiras (não incluído)"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControlLabel
                control={<Switch checked={planForm.destaque || false}
                  onChange={(e) => setPlanForm(p => ({ ...p, destaque: e.target.checked }))} />}
                label="Plano Destaque"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth type="number" label="Ordem" value={planForm.ordem}
                onChange={(e) => setPlanForm(p => ({ ...p, ordem: parseInt(e.target.value) || 0 }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth select label="Estado" value={planForm.status}
                onChange={(e) => setPlanForm(p => ({ ...p, status: e.target.value as any }))}>
                <MenuItem value="ativo">Ativo</MenuItem>
                <MenuItem value="inativo">Inativo</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialog({ open: false, plan: null })}>Cancelar</Button>
          <Button onClick={handleSavePlan} variant="contained" disabled={saving || !planForm.nome}>
            {saving ? 'A guardar...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FAQ Dialog */}
      <Dialog open={faqDialog.open} onClose={() => setFaqDialog({ open: false, faq: null })} maxWidth="md" fullWidth>
        <DialogTitle>{faqDialog.faq ? 'Editar FAQ' : 'Nova FAQ'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Categoria" value={faqForm.categoria}
            onChange={(e) => setFaqForm(p => ({ ...p, categoria: e.target.value }))}
            margin="normal" placeholder="Ex: Tickets, Geral, Pagamentos" />
          <TextField fullWidth label="Pergunta" value={faqForm.pergunta}
            onChange={(e) => setFaqForm(p => ({ ...p, pergunta: e.target.value }))}
            margin="normal" required />
          <TextField fullWidth multiline rows={6} label="Resposta" value={faqForm.resposta}
            onChange={(e) => setFaqForm(p => ({ ...p, resposta: e.target.value }))}
            margin="normal" required
            helperText="Pode usar **texto** para negrito" />
          <Box display="flex" gap={2} mt={2}>
            <TextField type="number" label="Ordem" value={faqForm.ordem}
              onChange={(e) => setFaqForm(p => ({ ...p, ordem: parseInt(e.target.value) || 0 }))}
              sx={{ width: 120 }} />
            <TextField select label="Estado" value={faqForm.status}
              onChange={(e) => setFaqForm(p => ({ ...p, status: e.target.value as any }))}
              sx={{ flexGrow: 1 }}>
              <MenuItem value="ativo">Ativo</MenuItem>
              <MenuItem value="inativo">Inativo</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFaqDialog({ open: false, faq: null })}>Cancelar</Button>
          <Button onClick={handleSaveFaq} variant="contained"
            disabled={saving || !faqForm.pergunta || !faqForm.resposta}>
            {saving ? 'A guardar...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CTA Dialog */}
      <Dialog open={ctaDialog.open} onClose={() => setCtaDialog({ open: false, cta: null })} maxWidth="md" fullWidth>
        <DialogTitle>{ctaDialog.cta ? 'Editar CTA' : 'Novo CTA'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth select label="Posição" value={ctaForm.posicao}
                onChange={(e) => setCtaForm(p => ({ ...p, posicao: e.target.value }))}>
                <MenuItem value="meio">Meio da página</MenuItem>
                <MenuItem value="final">Final da página</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Cor de Fundo" value={ctaForm.cor_fundo}
                onChange={(e) => setCtaForm(p => ({ ...p, cor_fundo: e.target.value }))}
                placeholder="pagali-primary, pagali-surface, ..." />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Título" value={ctaForm.titulo}
                onChange={(e) => setCtaForm(p => ({ ...p, titulo: e.target.value }))} required />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth multiline rows={3} label="Descrição" value={ctaForm.descricao}
                onChange={(e) => setCtaForm(p => ({ ...p, descricao: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Texto do Botão" value={ctaForm.btn_texto}
                onChange={(e) => setCtaForm(p => ({ ...p, btn_texto: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Link do Botão" value={ctaForm.btn_link}
                onChange={(e) => setCtaForm(p => ({ ...p, btn_link: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth type="number" label="Ordem" value={ctaForm.ordem}
                onChange={(e) => setCtaForm(p => ({ ...p, ordem: parseInt(e.target.value) || 0 }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth select label="Estado" value={ctaForm.status}
                onChange={(e) => setCtaForm(p => ({ ...p, status: e.target.value as any }))}>
                <MenuItem value="ativo">Ativo</MenuItem>
                <MenuItem value="inativo">Inativo</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCtaDialog({ open: false, cta: null })}>Cancelar</Button>
          <Button onClick={handleSaveCta} variant="contained" disabled={saving || !ctaForm.titulo}>
            {saving ? 'A guardar...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feature Dialog */}
      <Dialog
        open={featureDialog.open}
        onClose={() => setFeatureDialog({ open: false, feature: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {featureDialog.feature ? t('cms.editFeature') : t('cms.newFeature')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('cms.iconLucide')}
            value={featureForm.icone}
            onChange={(e) => setFeatureForm(prev => ({ ...prev, icone: e.target.value }))}
            margin="normal"
            required
            placeholder={t('cms.iconLucidePlaceholder')}
            helperText={t('cms.iconLucideHelperText')}
          />
          <TextField
            fullWidth
            label={t('cms.titleLabel')}
            value={featureForm.titulo}
            onChange={(e) => setFeatureForm(prev => ({ ...prev, titulo: e.target.value }))}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label={t('cms.descriptionField')}
            value={featureForm.descricao}
            onChange={(e) => setFeatureForm(prev => ({ ...prev, descricao: e.target.value }))}
            margin="normal"
            multiline
            rows={3}
          />
          <TextField
            fullWidth
            label={t('cms.linkOptional')}
            value={featureForm.link}
            onChange={(e) => setFeatureForm(prev => ({ ...prev, link: e.target.value }))}
            margin="normal"
            placeholder={t('cms.linkOptionalPlaceholder')}
          />
          <Box display="flex" gap={2} mt={2}>
            <TextField
              label={t('cms.bgColorLabel')}
              value={featureForm.cor_fundo}
              onChange={(e) => setFeatureForm(prev => ({ ...prev, cor_fundo: e.target.value }))}
              placeholder="bg-blue-100"
              sx={{ flexGrow: 1 }}
            />
            <TextField
              label={t('cms.textColorLabel')}
              value={featureForm.cor_texto}
              onChange={(e) => setFeatureForm(prev => ({ ...prev, cor_texto: e.target.value }))}
              placeholder="text-blue-600"
              sx={{ flexGrow: 1 }}
            />
          </Box>
          <Box display="flex" gap={2} mt={2}>
            <TextField
              type="number"
              label={t('cms.order')}
              value={featureForm.ordem}
              onChange={(e) => setFeatureForm(prev => ({ ...prev, ordem: parseInt(e.target.value) || 0 }))}
              sx={{ width: 100 }}
            />
            <TextField
              select
              label={t('common.status')}
              value={featureForm.status}
              onChange={(e) => setFeatureForm(prev => ({ ...prev, status: e.target.value as any }))}
              sx={{ flexGrow: 1 }}
            >
              <MenuItem value="ativo">{t('common.active')}</MenuItem>
              <MenuItem value="inativo">{t('common.inactive')}</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeatureDialog({ open: false, feature: null })}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveFeature}
            variant="contained"
            disabled={saving || !featureForm.titulo || !featureForm.icone}
          >
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PageEditor;
